import { get, omitBy } from "lodash/fp";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Checkbox,
  Container,
  Content,
  FlexboxGrid,
  Footer,
  Header,
  Pagination,
  Table,
  Input,
  InputGroup,
} from "rsuite";
import SearchIcon from "@rsuite/icons/Search";
import CloseIcon from "@rsuite/icons/Close";
import { ReportServices } from "../../../core/services/ReportServices";
import { UtilityServices } from "../../../core/services/UtilityServices";
import { AlertModal } from "../../components/Modals/AlertModal/AlertModal";
import { MediaPlayFields, MediaPlayListParams } from "./ReportDataTypes";
import { Permission } from "../../../core/types/PermissionInterface";
import "./Reports.css";
import MediaPlayFilters from "./MediaPlayFilters";
import {
  isEmptyVal,
  isNotEmpty,
  displayField,
  formatTimeField,
} from "../../../utils/helpers";
import CustomCell from "../../components/Elements/CustomCell";
import CustomHeaderCell from "../../components/Elements/CustomHeaderCell";
import CheckCell from "../../components/Elements/CheckCell";
import ReportBulkAction from "./ReportBulkAction";
import { ToastContainer } from "react-toastify";
import ImageModal from "../../components/Modals/ImageModal/ImageModal";
import {
  appConstObj,
  roleTypeArr,
  dateFormatObj,
} from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";
import { isEmpty } from "lodash";
import SongInfoCell from "../../components/Elements/SongInfoCell";
import CommercialInfoCell from "../../components/Elements/CommercialInfoCell";
import MediaPlay from "../../components/Elements/MediaPlay";
import MediaPlayerFormatter from "../../components/Elements/MediaPlayerFormatter";
import moment from "moment";
import { useSelector } from "react-redux";
import AudioPlayer from "../../components/Elements/AudioPlayer";
import { useMediaContext } from "../../../context/MediaContext";

interface ImageModal {
  openModal: () => void;
  removePic: () => void;
  hideDelete: () => void;
}

function MediaPlayReport() {
  const { t } = useTranslation();
  const { Cell, Column } = Table;
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(appConstObj.record_per_page);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<MediaPlayFields[]>([]);
  const [sort_by, setSortBy] = useState<any>();
  const [sort_order, setSortOrder] = useState<any>();
  const modalRef = useRef<ImageModal>(null);
  const [openPictureDelModal, setOpenPictureDelModal] = useState(false);
  const [smartSearchInput, setSmartSearch] = useState<string>("");
  const loggedInUserDetail = UtilityServices.getLoggedInUserDetails();
  const { playingMediaInfo, setPlayingMediaInfo, isPlaying, setIsPlaying } =
    useMediaContext();
  const company = useSelector((state: any) => state.company);
  const initSearchFormData = {
    company: getCompanyId(),
    store_group: "",
    region: "",
    store: UtilityServices.getLoggedInUser("store_id"),
    start_date: new Date(moment().format(dateFormatObj.moment_y_m_d)),
    end_date: new Date(moment().format(dateFormatObj.moment_y_m_d)),
    play_date: null,
    start_time: "",
    end_time: "",
    smart_search: "",
    media_search: "",
    media_category: "",
    media_nature: "",
    request_device_type: "",
  };

  const [formData, setFormData] =
    useState<MediaPlayListParams>(initSearchFormData);
  const navigate = useNavigate();
  const [checkedKeys, setCheckedKeys] = useState<any[]>([]);
  let checked = false;
  let indeterminate = false;
  const [failedImages, setFailedImages] = useState<any>([]);

  if (checkedKeys?.length === data?.length) {
    checked = true;
  } else if (checkedKeys?.length === 0) {
    checked = false;
  } else if (checkedKeys?.length > 0 && checkedKeys?.length < data?.length) {
    indeterminate = true;
  }

  const handleCheckAll = (value: any, checked: any) => {
    const keys = checked ? data.map((item) => item.id) : [];
    setCheckedKeys(keys);
  };

  const handleCheck = (value: number, checked: any) => {
    const keys = checked
      ? [...checkedKeys, value]
      : checkedKeys.filter((item) => item !== value);
    setCheckedKeys(keys);
  };

  async function getMediaPlayReport(params: MediaPlayListParams) {
    try {
      let compId: number = getCompanyId();
      if (!compId) {
        setData([]);
        return;
      }
      if (isEmptyVal(params.company)) {
        params.company = compId.toString();
      }
      setLoading(true);
      const res = await ReportServices.getMediaPlayReport(params);
      setData(get("data.data", res));
      setTotal(get("data.total", res));
      setCheckedKeys([]);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }

  function init() {}

  useEffect(() => {
    init();
    getMediaPlayReport(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  }, [company]);

  async function onExportReport() {
    try {
      await ReportServices.exportMediaPlayReport(getSearchParams());
    } catch (error) {
      console.log(error);
    }
  }

  function getFailedImages(val: any) {
    setFailedImages(val);
  }

  function getSearchParams(data?: any) {
    let paramsData = {
      ...formData,
      ...{ smart_search: smartSearchInput },
      ...{ company: getCompanyId() },
      ...{ sort_by, sort_order },
      ...{ rows },
      ...data,
    };
    let params: MediaPlayListParams = omitBy(isEmptyVal, paramsData);
    if (data?.page) {
      params.page = data?.page;
    }
    return params;
  }

  function onPageChange(page: number) {
    setPage(page);
    getMediaPlayReport(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  }

  function onSortChange(dataKey: any, sortType: any) {
    setPage(1);
    setSortBy(dataKey);
    setSortOrder(sortType);
    getMediaPlayReport(
      getSearchParams({
        page: 1,
        sort_by: dataKey,
        sort_order: sortType,
        rows: rows,
      })
    );
  }

  function submitSearch(formData: any) {
    setFormData(formData);
    getMediaPlayReport(getSearchParams(formData));
  }

  function genreTitle(rData: any) {
    let gData = "";
    if (!isEmpty(rData.genre_title)) gData += rData.genre_title;
    if (!isEmpty(rData.sub_genre_title))
      gData += " (" + rData.sub_genre_title + ")";

    return gData;
  }

  function clearSearch() {
    setSmartSearch("");
    setFormData(initSearchFormData);
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    getMediaPlayReport(
      getSearchParams({
        ...initSearchFormData,
        ...{
          page: 1,
          rows: appConstObj.record_per_page,
        },
      })
    );
  }

  const filterMedias = (e: any) => {
    if (e.key === "Enter" || e.type === "click") {
      getMediaPlayReport(
        getSearchParams({ smart_search: smartSearchInput, rows: rows })
      );
    }
  };

  const clearSmartSearch = () => {
    setSmartSearch("");
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    getMediaPlayReport(
      getSearchParams({
        smart_search: "",
        page: 1,
        sort_by: null,
        sort_order: null,
        rows: appConstObj.record_per_page,
      })
    );
  };

  const handleChangeRows = (rows: any) => {
    setRows(rows);
    setPage(1);
    getMediaPlayReport(
      getSearchParams({
        page: 1,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  };

  function getCompanyId() {
    let compId = null;
    if (UtilityServices.getLoggedInUser("user_type") === roleTypeArr.client) {
      compId = UtilityServices.getLoggedInUser("company_id");
    } else if (company?.id) {
      compId = company.id;
    }
    return compId;
  }

  return (
    <div className="media-play-report">
      <Container className="container">
        <Header className="header media-play-report-header">
          <FlexboxGrid justify="space-between">
            <FlexboxGrid.Item colspan={8}>
              <h4>
                {t("MEDIA")} {t("PLAY")} {t("REPORT")}
              </h4>
            </FlexboxGrid.Item>
          </FlexboxGrid>
        </Header>
        <Content className="media-play-report-content">
          {UtilityServices.hasLoggedInUserType(roleTypeArr.backend) && (
            <>
              <MediaPlayFilters onSubmit={submitSearch} onClear={clearSearch} />
              <div className="row media-play-report-controls-row">
                <ReportBulkAction
                  checkedKeys={checkedKeys}
                  setCheckedKeys={setCheckedKeys}
                  onExportReport={onExportReport}
                />
                <div className="col-md-4">
                  <div className="media-play-report-smart-search-wrap">
                  <InputGroup className="media-play-report-smart-search">
                    <Input
                      className="form-control"
                      placeholder={t("SMART_SEARCH_BY", {
                        entity:
                          t("MEDIA").toLowerCase() +
                          " " +
                          t("NAME").toLowerCase() +
                          ", " +
                          t("SINGER").toLowerCase() +
                          ", " +
                          t("OR").toLowerCase() +
                          " " +
                          t("NAME").toLowerCase(),
                      })}
                      value={smartSearchInput}
                      onChange={(val: any) => setSmartSearch(val)}
                      onKeyDown={(e: any) => filterMedias(e)}
                    />
                    <InputGroup.Button
                      title={t("SEARCH")}
                      onClick={(e: any) => filterMedias(e)}
                    >
                      <SearchIcon />
                    </InputGroup.Button>
                    <InputGroup.Button
                      title={t("CLEAR")}
                      onClick={() => clearSmartSearch()}
                    >
                      <CloseIcon />
                    </InputGroup.Button>
                  </InputGroup>
                  </div>
                </div>
              </div>
            </>
          )}

          <div
            className={
              total > 0
                ? "media-play-report-table"
                : "media-play-report-empty-wrap"
            }
          >
            <Table
              height={400}
              fillHeight={true}
              data={data}
              loading={loading}
              onSortColumn={onSortChange}
              sortColumn={sort_by}
              bordered={true}
              cellBordered
              sortType={sort_order}
              hover={true}
              renderEmpty={() => (
                <div className="media-play-report-empty-state">
                  {getCompanyId() !== null ? (
                    <>
                      <div className="media-play-report-empty-icon">0</div>
                      <h4 className="media-play-report-empty-title">
                        {t("NO_ENTITY_FOUND", {
                          entity: t("RECORD"),
                        })}
                      </h4>
                      <p className="media-play-report-empty-text">
                        {t("ADJUST_FILTER")}
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="media-play-report-empty-icon">?</div>
                      <h4 className="media-play-report-empty-title">
                        {t("PLEASE_SELECT_FIELD", { field: t("COMPANY") })}
                      </h4>
                    </>
                  )}
                </div>
              )}
            >
              <Column width={40} align="center" fixed>
                <CustomHeaderCell>
                  <div>
                    <Checkbox
                      inline
                      checked={checked}
                      indeterminate={indeterminate}
                      onChange={handleCheckAll}
                    />
                  </div>
                </CustomHeaderCell>
                <CheckCell
                  dataKey="id"
                  checkedKeys={checkedKeys}
                  onChange={handleCheck}
                />
              </Column>
              <Column width={60} align="center" fixed>
                <CustomHeaderCell>{t("PLAY")}</CustomHeaderCell>
                <MediaPlayerFormatter
                  rowData={(rowData: any) => rowData}
                  mediaUrlKey={"media_url"}
                  mediaImageKey={"media_image"}
                />
              </Column>
              <Column width={200} sortable resizable fixed>
                <CustomHeaderCell>{t("NAME")}</CustomHeaderCell>
                <CustomCell dataKey="title">
                  {(rowData: any) => (
                    <span title={`${rowData.title}`}>
                      {displayField(rowData.title)}
                    </span>
                  )}
                </CustomCell>
              </Column>
              <Column width={80} sortable resizable>
                <CustomHeaderCell>{t("CATEGORY")}</CustomHeaderCell>
                <CustomCell dataKey="media_category">
                  {(rowData: any) => (
                    <span>{displayField(rowData.media_category)}</span>
                  )}
                </CustomCell>
              </Column>
              <Column width={80}>
                <CustomHeaderCell>{t("TYPE")}</CustomHeaderCell>
                <CustomCell dataKey="media_real_nature">
                  {(rowData: any) => (
                    <span>{displayField(rowData.media_real_nature)}</span>
                  )}
                </CustomCell>
              </Column>
              <Column width={100}>
                <CustomHeaderCell>
                  {t("START")} {t("TIME")}
                </CustomHeaderCell>
                <CustomCell dataKey="media_start_time">
                  {(rowData: any) => (
                    <span>
                      {formatTimeField(rowData.media_start_time, "12hr")}
                    </span>
                  )}
                </CustomCell>
              </Column>
              <Column width={100}>
                <CustomHeaderCell>
                  {t("END")} {t("TIME")}
                </CustomHeaderCell>
                <CustomCell dataKey="media_end_time">
                  {(rowData: any) => (
                    <span>
                      {formatTimeField(rowData.media_end_time, "12hr")}
                    </span>
                  )}
                </CustomCell>
              </Column>
              <Column width={100}>
                <CustomHeaderCell>
                  {t("SCHEDULE")} {t("TIME")}
                </CustomHeaderCell>
                <CustomCell dataKey="comm_shall_start_at">
                  {(rowData: any) => (
                    <span>
                      {displayField(
                        formatTimeField(rowData.comm_shall_start_at, "12hr")
                      )}
                    </span>
                  )}
                </CustomCell>
              </Column>
              <Column width={100} resizable>
                <CustomHeaderCell>
                  {t("EXACT")} {t("NATURE")}
                </CustomHeaderCell>
                <CustomCell dataKey="play_on_exact_time">
                  {(rowData: any) => (
                    <span>
                      {rowData?.play_on_exact_time === "1" ? t("YES") : t("NO")}
                    </span>
                  )}
                </CustomCell>
              </Column>
              <Column width={80}>
                <CustomHeaderCell>{t("VOLUME")}</CustomHeaderCell>
                <CustomCell dataKey="average_volume">
                  {(rowData: any) => (
                    <span>{displayField(rowData.average_volume)}</span>
                  )}
                </CustomCell>
              </Column>
              <Column width={80}>
                <CustomHeaderCell>{t("DEVICE")}</CustomHeaderCell>
                <CustomCell dataKey="request_device">
                  {(rowData: any) => (
                    <span>{displayField(rowData.request_device)}</span>
                  )}
                </CustomCell>
              </Column>
              <Column width={60}>
                <CustomHeaderCell>{t("VERSION")}</CustomHeaderCell>
                <CustomCell dataKey="application_version">
                  {(rowData: any) => (
                    <span>{displayField(rowData.application_version)}</span>
                  )}
                </CustomCell>
              </Column>
              <Column width={80}>
                <CustomHeaderCell>{t("IP")}</CustomHeaderCell>
                <CustomCell dataKey="request_ip">
                  {(rowData: any) => (
                    <span>{displayField(rowData.request_ip)}</span>
                  )}
                </CustomCell>
              </Column>
              <Column width={80}>
                <CustomHeaderCell>
                  {t("SONG")} {t("INFO")}
                </CustomHeaderCell>
                <SongInfoCell
                  rowData={(rowData: any) => rowData}
                  showData={[
                    "singer1",
                    "era",
                    "mood",
                    "lyricist",
                    "composer",
                    "tempo",
                    "media_type",
                    "sourced_from",
                    "rating_chart",
                  ]}
                />
              </Column>
              <Column width={80}>
                <CustomHeaderCell>
                  {t("COMM")} {t("INFO")}
                </CustomHeaderCell>
                <CommercialInfoCell
                  rowData={(rowData: any) => rowData}
                  showData={[
                    "campaign_name",
                    "brand_name",
                    "commercial_use_for",
                  ]}
                />
              </Column>
              <Column width={80} sortable>
                <CustomHeaderCell>
                  {t("USE")} {t("TYPE")}
                </CustomHeaderCell>
                <CustomCell dataKey="commercial_use_for">
                  {(rowData: any) => (
                    <span>
                      {rowData.commercial_use_for == "external"
                        ? t("Ext")
                        : t("Int")}
                    </span>
                  )}
                </CustomCell>
              </Column>
              <Column width={150}>
                <CustomHeaderCell>{t("STORE")}</CustomHeaderCell>
                <CustomCell dataKey="store_name">
                  {(rowData: any) => (
                    <span>{displayField(rowData.store_name)}</span>
                  )}
                </CustomCell>
              </Column>
            </Table>
          </div>
        </Content>
        <Footer className="footer">
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="xs"
            layout={["total", "-", "limit", "|", "pager", "skip"]}
            total={total}
            limitOptions={appConstObj.limit_options}
            limit={rows}
            activePage={page}
            onChangePage={onPageChange}
            onChangeLimit={handleChangeRows}
          />
        </Footer>
        <AudioPlayer />
        <ToastContainer />
      </Container>
    </div>
  );
}

export default MediaPlayReport;
